'use client'

import type { ReactNode } from 'react'

import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

interface OptionRowProps {
  selected: boolean
  onSelect: () => void
  disabled?: boolean
  children: ReactNode
  /** Rendered on the right instead of the left, matching the child picker in the designs. */
  markerSide?: 'start' | 'end'
}

/**
 * A selectable row with a radio marker.
 *
 * Composed here rather than imported: `@ui/web` has no `RadioGroup`, and these rows are a card with
 * its own selected background, not the bare control a shared radio would give. Add a `RadioGroup` to
 * `@ui/web` and this becomes a thin wrapper over it.
 *
 * `role="radio"` with `aria-checked` rather than a styled `<input>` so the whole card is the target;
 * the parent list carries `role="radiogroup"`.
 */
export function OptionRow({ selected, onSelect, disabled, children, markerSide = 'start' }: OptionRowProps) {
  const marker = (
    <span
      aria-hidden
      className={cn(
        'srd:flex srd:size-5 srd:shrink-0 srd:items-center srd:justify-center srd:rounded-full srd:border-2',
        selected ? 'srd:border-blue-100' : 'srd:border-grey-40',
      )}
    >
      {selected && <span className='srd:bg-blue-100 srd:size-2.5 srd:rounded-full' />}
    </span>
  )

  return (
    <button
      type='button'
      role='radio'
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        typographyVariants({ variant: 'body-md' }),
        'srd:flex srd:w-full srd:items-center srd:gap-x-3 srd:rounded-xl srd:px-4 srd:py-3 srd:text-left srd:transition-colors',
        'srd:disabled:cursor-not-allowed srd:disabled:opacity-50',
        selected ? 'srd:bg-blue-10 srd:text-neutral-black' : 'srd:text-neutral-black srd:hover:bg-grey-10',
      )}
    >
      {markerSide === 'start' && marker}
      <span className='srd:flex srd:w-full srd:min-w-0 srd:items-center srd:gap-x-3'>{children}</span>
      {markerSide === 'end' && marker}
    </button>
  )
}
